import { FC, useEffect, useMemo } from 'react';
import Typography from '@components/common/typography';
import Input from '@components/common/input';
import styles from './styles.module.scss';
import { IModalComponentProps } from '@context/modal_context';
import { prettifyAmount } from '@utils/index';
import { useForm } from '@hooks/helpers/useForm';
import FormField from '@components/form_field';
import { useLocales } from '@hooks/helpers/useLocales';
import { FormProvider, useWatch } from 'react-hook-form';
import {
    ETransactionAction,
    ETransactionMethod,
    ETransactionType,
    TPublicGiftCard
} from '@xeppt/xeppt-sdk/types';
import { EFundsOperation } from '@enum';
import { useUserContext } from '@hooks/context/useUserContext';
import Button from '@components/common/button';
import { useSendAndRequestModals } from '@hooks/modals/useSendAndRequestModals';
import { useIsFirstRender } from '@mantine/hooks';
import { apiAccountService } from '@api';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useFrequency } from '@hooks/helpers/useFrequency';
import { useDebounceValue } from 'usehooks-ts';
import { useGiftCardsModals } from '@hooks/modals/useGiftCardsModals';
import { useQueryParams } from '@hooks/helpers/useQueryParams';

// from_charged is a new backend param not yet in the SDK type — cast to pass it.
type TFeePayloadExt = Parameters<typeof apiAccountService.calculateLoyaltyFee>[0] & {
    from_charged?: boolean;
};

const RedeemGiftCardModal: FC<
    IModalComponentProps<{
        amount: string;
        reference: string;
        giftCard: TPublicGiftCard;
        refetchGiftCards?: () => void;
    }>
> = ({ modalData, changeModalData, handleChangeDisable }) => {
    const [redeemId] = useQueryParams(['redeem_id']);
    const isFirstRender = useIsFirstRender();
    const { limits } = useUserContext();
    const { validationLocale, placeholderLocale, submitLocale } = useLocales();
    const form = useForm({
        defaultValues: {
            amount: '',
            amount_result: '',
            reference: ''
        }
    });
    const watchedAmount = useWatch({ control: form.control, name: 'amount' });
    const watchedReference = useWatch({ control: form.control, name: 'reference' });
    // amountDebounced carries the gross amount the user typed; triggers the fee calc.
    const [amountDebounced, setAmountDebounced] = useDebounceValue(0, 300);
    const { handleOpenEstimatedFeesModal } = useSendAndRequestModals();
    const { amountValidation } = useFrequency({
        isFrequencyExist: false,
        method: EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM
    });
    const { handleOpenRedeemGiftCardModal } = useGiftCardsModals({
        refetchGiftCards: modalData?.refetchGiftCards
    });

    useEffect(() => {
        handleChangeDisable(!form.formState.isValid);
    }, [form.formState.isValid]);

    useEffect(() => {
        changeModalData({
            ...modalData,
            amount: form.getValues().amount,
            reference: form.getValues().reference
        });
    }, [watchedAmount, watchedReference]);

    const { data: pointsFeeData, handleRequest: handleGetSummary } = useApiQuery({
        // Anchor to the keyed gross via from_charged so the backend (CalculateFundsFromCharged)
        // returns the exact net/fee pair without rounding drift.
        // data.total = GROSS drawn off card; data.amount = NET the user receives; data.fee = fee.
        method: (data?: { amount: number }) => {
            const values = form.getValues();
            return apiAccountService.calculateLoyaltyFee({
                method: ETransactionMethod.GIFT_CARD,
                action: ETransactionAction.GIFT_CARD_REDEEM,
                // Redeem is the DEBIT leg (loyalty key 8_2_14). CREDIT built 8_1_14 → no
                // plan found → fee resolved to $0 in the pre-redeem preview (Bugs 10.0 #2).
                type: ETransactionType.DEBIT,
                fee_included: false,
                amount: Number(data?.amount ?? values.amount),
                from_charged: true
            } as TFeePayloadExt);
        },
        isInitialRequest: false,
        onSuccess: async (data) => {
            if (data.amount > 0 && data.total > 0) {
                // Validate the GROSS (drawn off card) against per-transfer/daily limits.
                const error = amountValidation(data.total.toString());
                // amount = GROSS (what is drawn off the card); amount_result = NET (received).
                form.setValue('amount', data.total.toFixed(2));
                form.setValue('amount_result', data.amount.toFixed(2));
                if (error) {
                    form.setError('amount', {
                        message: error
                    });
                    handleChangeDisable(true);
                } else {
                    form.clearErrors('amount');
                    form.clearErrors('amount_result');
                    if (!!form.getValues().reference) {
                        await form.trigger();
                    }
                    handleChangeDisable(false);
                }
            }
        }
    });

    useEffect(() => {
        if (!isFirstRender) {
            handleGetSummary({ amount: amountDebounced });
        }
    }, [amountDebounced]);

    const currentLimits = useMemo(() => {
        return limits?.[EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM];
    }, [limits]);

    const isLimitsExists = useMemo(() => {
        return (
            currentLimits?.per_transfer !== 0 ||
            currentLimits?.daily !== 0 ||
            currentLimits?.weekly !== 0 ||
            currentLimits?.monthly !== 0
        );
    }, []);

    return (
        <FormProvider {...form}>
            <div className={styles.wrapper}>
                <Typography className={styles.title} variant="h4">
                    Redeem gift card
                </Typography>
                {isLimitsExists && (
                    <div className={styles.limits_wrapper}>
                        <div className={styles.title}>
                            <Typography variant="body4" weight="semibold">
                                Limits
                            </Typography>{' '}
                        </div>
                        <div className={styles.limits_values}>
                            {currentLimits?.per_transfer !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Maximum amount
                                    </Typography>
                                    <Typography
                                        weight="semibold"
                                        variant="body4"
                                        className={styles.active_amount}>
                                        <b>{(currentLimits?.per_transfer || 0)?.toFixed(2)} CAD</b>
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.amount_transfer_min !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Minimum amount
                                    </Typography>
                                    <Typography
                                        weight="semibold"
                                        variant="body4"
                                        className={styles.active_amount}>
                                        <b>
                                            {(currentLimits?.amount_transfer_min || 0)?.toFixed(2)}{' '}
                                            CAD
                                        </b>
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.daily !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Daily
                                    </Typography>
                                    <Typography variant="body4" className={styles.active_amount}>
                                        <b>
                                            {(
                                                (currentLimits?.daily || 0) -
                                                (currentLimits?.daily_used || 0)
                                            )?.toFixed(2)}{' '}
                                            CAD
                                        </b>{' '}
                                        / {(currentLimits?.daily || 0)?.toFixed(2)} CAD
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.weekly !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Weekly
                                    </Typography>
                                    <Typography variant="body4" className={styles.active_amount}>
                                        <b>
                                            {(
                                                (currentLimits?.weekly || 0) -
                                                (currentLimits?.weekly_used || 0)
                                            )?.toFixed(2)}{' '}
                                            CAD
                                        </b>{' '}
                                        / {(currentLimits?.weekly || 0)?.toFixed(2)} CAD
                                    </Typography>
                                </div>
                            )}
                            {currentLimits?.monthly !== 0 && (
                                <div className={styles.limit_item}>
                                    <Typography variant="body4" className={styles.active_title}>
                                        Monthly
                                    </Typography>
                                    <Typography variant="body4" className={styles.active_amount}>
                                        <b>
                                            {(
                                                (currentLimits?.monthly || 0) -
                                                (currentLimits?.monthly_used || 0)
                                            )?.toFixed(2)}{' '}
                                            CAD
                                        </b>{' '}
                                        / {(currentLimits?.monthly || 0)?.toFixed(2)} CAD
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className={styles.balance_wrapper}>
                    <div className={styles.balance}>
                        <Typography variant="body3">
                            Gift card balance: $
                            <b>
                                {prettifyAmount(
                                    (modalData?.giftCard?.available_amount || 0).toFixed(2) || ''
                                )}
                            </b>
                        </Typography>
                    </div>
                </div>
                <div className={styles.inputs_wrapper}>
                    <div className={styles.input_wrapper}>
                        <FormField
                            name="amount"
                            renderComponent={({ value, ...props }) => (
                                <Input
                                    label="Amount to redeem"
                                    full
                                    onlyNumbers
                                    rightIcon="canadian"
                                    placeholder={placeholderLocale('amount_placeholder')}
                                    fixedAmount
                                    prefix="CAD"
                                    hideErrorIcon
                                    value={value}
                                    onBlur={() => {
                                        setAmountDebounced(Number(value));
                                    }}
                                    {...props}
                                />
                            )}
                            rules={{
                                required: { value: true, message: validationLocale('amount') },
                                validate: (value: string) => {
                                    const amount = Number(value);
                                    if (
                                        (amount || 0) <= 0 ||
                                        amount > modalData?.giftCard?.available_amount
                                    ) {
                                        return validationLocale('amount');
                                    }
                                }
                            }}
                        />
                        <FormField
                            name="reference"
                            renderComponent={({ error, helperText, ...props }) => (
                                <Input
                                    label="Reference"
                                    full
                                    placeholder="Enter the reference"
                                    hideErrorIcon
                                    {...props}
                                    error={error}
                                    helperText={
                                        error
                                            ? helperText
                                            : 'Indicate the goods or services the gift card is redeemed for (e.g., Tire replacement)'
                                    }
                                />
                            )}
                            rules={{
                                required: { value: true, message: 'Invalid reference' }
                            }}
                        />
                    </div>
                    <div className={styles.fee_wrapper}>
                        <Typography variant="body4">
                            XEPPT fee:{' '}
                            <b>{(pointsFeeData?.fee || 0).toFixed(2)} CAD</b>
                        </Typography>
                        {pointsFeeData && (
                            <Typography variant="body4">
                                You receive:{' '}
                                <b>{(pointsFeeData.amount || 0).toFixed(2)} CAD</b>
                            </Typography>
                        )}
                        <Button
                            leftIcon="info"
                            variant="text-gold"
                            size="small"
                            onClick={() =>
                                handleOpenEstimatedFeesModal({
                                    amount: Number(form.getValues().amount),
                                    method: EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM,
                                    fee: pointsFeeData?.fee || 0,
                                    onClose: () =>
                                        redeemId &&
                                        handleOpenRedeemGiftCardModal(redeemId.replaceAll(' ', '+'))
                                })
                            }>
                            {submitLocale('get_info')}
                        </Button>
                    </div>
                </div>
            </div>
        </FormProvider>
    );
};

export default RedeemGiftCardModal;
